/**
 * Toast Context for displaying notifications
 *
 * Provides a simple toast notification system for displaying
 * error messages, success notifications, and status updates.
 */

import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  ReactNode,
} from 'react';

/**
 * Toast type
 */
export type ToastType = 'info' | 'success' | 'warning' | 'error';

/**
 * Toast message
 */
export interface Toast {
  id: string;
  type: ToastType;
  message: string;
  title?: string;
  duration: number;
  createdAt: number;
}

/**
 * Toast options
 */
export interface ToastOptions {
  title?: string;
  duration?: number;
}

/**
 * Toast context value
 */
export interface ToastContextValue {
  toasts: Toast[];
  addToast: (type: ToastType, message: string, options?: ToastOptions) => string;
  removeToast: (id: string) => void;
  clearToasts: () => void;

  // Convenience methods
  info: (message: string, options?: ToastOptions) => string;
  success: (message: string, options?: ToastOptions) => string;
  warning: (message: string, options?: ToastOptions) => string;
  error: (message: string, options?: ToastOptions) => string;
}

/**
 * Default toast duration (5 seconds)
 */
const DEFAULT_DURATION = 5000;

/**
 * Maximum number of toasts
 */
const MAX_TOASTS = 5;

/**
 * Toast context
 */
const ToastContext = createContext<ToastContextValue | null>(null);

/**
 * Generate toast ID
 */
function generateToastId(): string {
  return `toast_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
}

/**
 * Toast provider props
 */
interface ToastProviderProps {
  children: ReactNode;
  maxToasts?: number;
}

/**
 * Toast provider component
 */
export function ToastProvider({ children, maxToasts = MAX_TOASTS }: ToastProviderProps) {
  const [toasts, setToasts] = useState<Toast[]>([]);

  /**
   * Add a toast
   */
  const addToast = useCallback(
    (type: ToastType, message: string, options?: ToastOptions): string => {
      const id = generateToastId();
      const toast: Toast = {
        id,
        type,
        message,
        title: options?.title,
        duration: options?.duration ?? DEFAULT_DURATION,
        createdAt: Date.now(),
      };

      setToasts((prev) => {
        const newToasts = [...prev, toast];
        // Keep only the latest N toasts
        if (newToasts.length > maxToasts) {
          return newToasts.slice(-maxToasts);
        }
        return newToasts;
      });

      return id;
    },
    [maxToasts]
  );

  /**
   * Remove a toast
   */
  const removeToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  /**
   * Clear all toasts
   */
  const clearToasts = useCallback(() => {
    setToasts([]);
  }, []);

  /**
   * Convenience methods
   */
  const info = useCallback(
    (message: string, options?: ToastOptions) => addToast('info', message, options),
    [addToast]
  );

  const success = useCallback(
    (message: string, options?: ToastOptions) => addToast('success', message, options),
    [addToast]
  );

  const warning = useCallback(
    (message: string, options?: ToastOptions) => addToast('warning', message, options),
    [addToast]
  );

  const error = useCallback(
    (message: string, options?: ToastOptions) => addToast('error', message, options),
    [addToast]
  );

  // Auto-remove toasts after their duration
  useEffect(() => {
    const intervals: NodeJS.Timeout[] = [];

    toasts.forEach((toast) => {
      if (toast.duration > 0) {
        const elapsed = Date.now() - toast.createdAt;
        const remaining = toast.duration - elapsed;

        if (remaining > 0) {
          const timeout = setTimeout(() => {
            removeToast(toast.id);
          }, remaining);
          intervals.push(timeout);
        } else {
          removeToast(toast.id);
        }
      }
    });

    return () => {
      intervals.forEach((timeout) => clearTimeout(timeout));
    };
  }, [toasts, removeToast]);

  const contextValue: ToastContextValue = {
    toasts,
    addToast,
    removeToast,
    clearToasts,
    info,
    success,
    warning,
    error,
  };

  return (
    <ToastContext.Provider value={contextValue}>
      {children}
    </ToastContext.Provider>
  );
}

/**
 * Hook to use toast context
 */
export function useToast(): ToastContextValue {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
}

export default ToastContext;
